import { catchError, mergeMap } from 'rxjs/operators';
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription, throwError } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConfigService } from '../../../core/services/app-config.service';
import { RechtRubrikService } from './recht-rubrik/recht-rubrik.service';
import { keysToTranslate } from 'i18n/keys-to-translate';
import {
  GbpfandrechtService,
  GbrechtService,
  GbstandardrechtService,
  HistInfo,
  LastRechtGrundstueckEntryMeta,
  PfandrechtMasterDetailEntryMeta,
  RechtMasterId,
  RechtMasterListEntry,
  RechtVersionId,
  StandardrechtMasterDetailEntryMeta
} from '../../../core/gb-api';
import { TimepointService } from '../../../core/services/timepoint.service';
import { AppPermissionService } from '../../../core/services/app-permission.service';
import { DokulinkItemBuilderFactory } from '../../../shared/components/dokulink/dokulinkItemBuilderFactory';
import { CapExceptionHandler } from '../../../core/extension/exceptions/cap-exception-handler';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { TitleFunctionbarComponent } from '../../../shared/components/title-functionbar/title-functionbar.component';
import { MapsComponent } from '../../../shared/widgets/maps/maps.component';
import { DokulinkComponent } from '../../../shared/components/dokulink/dokulink.component';
import { AuszugComponent } from '../../../shared/components/auszug/auszug.component';
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap';
import { StandardrechtKopfdatenComponent } from '../standardrecht/standardrecht-kopfdaten/standardrecht-kopfdaten.component';
import { PfandrechtKopfdatenComponent } from '../pfandrecht/pfandrecht-kopfdaten/pfandrecht-kopfdaten.component';
import { FormsModule } from '@angular/forms';
import { HistoryDirective } from '../../../shared/directives/history.directive';
import { MetaDisablerDirective } from '../../../shared/directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../../shared/directives/meta-validator.directive';
import { RechtRubrikComponent } from './recht-rubrik/recht-rubrik.component';
import { CapSpinnerSmallComponent } from '../../../shared/components/cap-spinner-small/cap-spinner-small.component';
import { TranslatePipe } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { FormatRechteIdPipe } from '../../../shared/pipes/format/format-rechte-id.pipe';
import RechtArtEnum = StandardrechtMasterDetailEntryMeta.RechtArtEnum;
import BelastungArtEnum = LastRechtGrundstueckEntryMeta.BelastungArtEnum;
import HistEnumEnum = HistInfo.HistEnumEnum;
import DokulinkVerfuegbarEnum = StandardrechtMasterDetailEntryMeta.DokulinkVerfuegbarEnum;

@Component({
  selector: 'cap-recht-detail',
  templateUrl: './recht-detail.component.html',
  styleUrls: ['./recht-detail.component.scss'],
  imports: [
    ContentHeaderComponent,
    TitleComponent,
    TitleFunctionbarComponent,
    MapsComponent,
    DokulinkComponent,
    AuszugComponent,
    NgbNav,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavLink,
    NgbNavLinkBase,
    NgbNavContent,
    StandardrechtKopfdatenComponent,
    PfandrechtKopfdatenComponent,
    FormsModule,
    HistoryDirective,
    MetaDisablerDirective,
    MetaValidatorDirective,
    NgbNavOutlet,
    RechtRubrikComponent,
    CapSpinnerSmallComponent,
    TranslatePipe,
    AsyncPipe,
    FormatRechteIdPipe
  ]
})
export class RechtDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  pfandrecht$: Observable<PfandrechtMasterDetailEntryMeta>;
  standardrecht$: Observable<StandardrechtMasterDetailEntryMeta>;
  rechtVersionId: RechtVersionId;
  rechtMasterId: RechtMasterId;
  ereid: string;
  entry: RechtMasterListEntry;

  error: string;
  histEnum: HistEnumEnum;
  mapsVerfuegbar = true;
  dokulinkVerfuegbar: DokulinkVerfuegbarEnum = 'DOKULINK_VORHANDEN';

  stillLoading = true;

  private subscription: Subscription;

  constructor(
    private rechtService: GbrechtService,
    private pfandrechtService: GbpfandrechtService,
    private standardrechtService: GbstandardrechtService,
    private appConfigService: AppConfigService,
    public permissionService: AppPermissionService,
    private route: ActivatedRoute,
    private router: Router,
    private service: RechtRubrikService,
    private timepointService: TimepointService,
    protected dokuLinkBuilderFactory: DokulinkItemBuilderFactory,
    protected cd: ChangeDetectorRef,
    private exceptionHandler: CapExceptionHandler
  ) {
    keysToTranslate<BelastungArtEnum | RechtArtEnum | string>([
      'BELASTET',
      'BERECHTIGT',
      'BELASTET_BERECHTIGT',
      'DOMINANTINDET',
      'DIENSTBARKEIT',
      'VORMERKUNG',
      'ANMERKUNG',
      'GRUNDLAST',
      'PFANDRECHT',
      'SDR_DIENSTBARKEIT',
      'AuszugZuDIENSTBARKEITErstellen',
      'AuszugZuVORMERKUNGErstellen',
      'AuszugZuANMERKUNGErstellen',
      'AuszugZuGRUNDLASTErstellen',
      'AuszugZuPFANDRECHTErstellen',
      'AuszugZuSDR_DIENSTBARKEITErstellen',
      'AuszugZuEIGENTUMANTEILErstellen'
    ]);
  }

  static getRechtVersionId(id: string): RechtVersionId {
    return { id };
  }

  static getRechtMasterId(id: string): RechtMasterId {
    return { id };
  }

  ngOnInit(): void {
    this.subscription = this.appConfigService.einstellungHistorischeDaten$.subscribe((h) => {
      this.loadData();
    });
  }

  getRubriken(rechtArt: RechtArtEnum) {
    return this.service.getRubriken(rechtArt);
  }

  setShowDokulink(event: DokulinkVerfuegbarEnum) {
    this.dokulinkVerfuegbar = event;
  }

  isDokulinkMenuDisabled(): boolean {
    return this.dokulinkVerfuegbar === undefined || this.dokulinkVerfuegbar === 'DOKULINK_NICHT_VORHANDEN';
  }

  setShowMap(event: boolean) {
    this.mapsVerfuegbar = event;
  }

  isMapAvailable(): boolean {
    return this.mapsVerfuegbar;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  private loadData() {
    this.route.queryParams
      .pipe(
        mergeMap((params) => {
          this.rechtVersionId = RechtDetailComponent.getRechtVersionId(params['versionId']);
          this.rechtMasterId = RechtDetailComponent.getRechtMasterId(params['id']);
          this.ereid = params['ereid'];
          if (this.rechtVersionId && this.rechtVersionId.id) {
            return this.rechtService.loadRechtListEntryById(
              this.rechtMasterId.id,
              this.timepointService.timepointCtx.timepointIso,
              this.timepointService.timepointCtx.timepointId
            );
          } else if (this.ereid) {
            return this.rechtService.loadRechtListEntryByFachId(
              this.ereid,
              this.timepointService.timepointCtx.timepointIso,
              this.timepointService.timepointCtx.timepointId
            );
          }
        })
      )
      .subscribe({
        next: (entry) => {
          if (!this.rechtVersionId || !this.rechtVersionId.id) {
            this.rechtVersionId = RechtDetailComponent.getRechtVersionId(entry.version.id.id);
          }
          this.entry = entry;
          if (entry.art === 'PFANDRECHT') {
            const timepointCtx = this.timepointService.timepointCtx;
            this.pfandrecht$ = this.pfandrechtService
              .loadPfandrechtById(
                entry.id.id, //
                this.appConfigService.einstellungHistorischeDaten, //
                this.rechtVersionId.id, //
                timepointCtx.timepointIso, //
                timepointCtx.timepointId //
              )
              .pipe(
                catchError((err) => {
                  this.stillLoading = false;
                  if (err.status !== 403) {
                    return throwError(err);
                  }
                })
              );
          } else {
            const z = this.timepointService.timepointCtx;
            this.standardrecht$ = this.standardrechtService
              .loadStandardrechtById(
                entry.id.id, //
                this.appConfigService.einstellungHistorischeDaten, //
                this.rechtVersionId.id, //
                z.timepointIso, //
                z.timepointId //
              )
              .pipe(
                catchError((err) => {
                  this.stillLoading = false;
                  if (err.status !== 403) {
                    return throwError(err);
                  }
                })
              );
          }
        },
        error: (err) => this.handleError(err)
      });
  }

  private handleError(err: any) {
    if (err.status === 403) {
      this.router.navigate(['/security/forbidden']);
    } else if (err.status === 404 || err.status === 400) {
      this.router.navigate(['/home/notfound']);
    } else {
      this.exceptionHandler.handleError(err);
    }
  }
}
